import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { CategoryNode, CategoryTree, MerchantOption } from './types.ts';
import { MarketPicker } from './ui.tsx';

export function Categories() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const market = query.get('market') ?? '';
  const open = query.get('category');
  const tree = useOp<CategoryTree>('admin.categories.tree', market ? { market } : {}, changes);
  const nodes = tree.data?.nodes ?? [];
  const [cursor, setCursor] = useState(0);
  const [creating, setCreating] = useState(false);

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, nodes.length)),
      k: () => setCursor((c) => step(c, -1, nodes.length)),
      enter: () => nodes[cursor] && setQuery({ category: nodes[cursor]!.ref }),
      o: () => nodes[cursor] && setQuery({ category: nodes[cursor]!.ref }),
      n: () => tree.data?.canEdit && setCreating(true),
    },
    !open && !creating,
  );

  return (
    <section className="page narrow">
      <header className="page-head">
        <div>
          <h1>Categories</h1>
          <p className="muted">One tree per market. Merchants can be in several categories.</p>
        </div>
        <div className="toolbar">
          <MarketPicker value={market || tree.data?.market || ''} onChange={(code) => setQuery({ market: code || null, category: null })} />
          {tree.data?.canEdit ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              New category <kbd className="kbd">n</kbd>
            </button>
          ) : null}
        </div>
      </header>
      {tree.error ? <ErrorBox error={tree.error} onRetry={tree.reload} /> : null}
      {!tree.data && tree.loading ? <Loading /> : null}
      {tree.data && nodes.length === 0 ? <Empty>No categories in {tree.data.market} yet.</Empty> : null}
      {nodes.length ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Slug</th>
              <th scope="col" className="num">Merchants</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((node, index) => (
              <tr key={node.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setQuery({ category: node.ref })} onMouseEnter={() => setCursor(index)}>
                <td style={{ paddingLeft: 10 + node.depth * 20 }}>
                  {node.depth ? <span className="muted" aria-hidden="true">└ </span> : null}
                  <button type="button" className="link-button strong" onClick={() => setQuery({ category: node.ref })}>
                    {node.name}
                  </button>
                </td>
                <td className="muted">/category/{node.slug}</td>
                <td className="num">{node.merchants}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {creating && tree.data ? <CategoryForm market={tree.data.market} nodes={nodes} onClose={() => setCreating(false)} /> : null}
      {open && tree.data ? <CategoryDrawer node={nodes.find((n) => n.ref === open) ?? null} market={tree.data.market} nodes={nodes} canEdit={tree.data.canEdit} onClose={() => setQuery({ category: null })} /> : null}
    </section>
  );
}

/** Ids of a node and everything below it, so they cannot be chosen as its parent. */
function subtree(nodes: CategoryNode[], ref: string): Set<string> {
  const out = new Set([ref]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const node of nodes) if (node.parentRef && out.has(node.parentRef) && !out.has(node.ref)) (out.add(node.ref), (grew = true));
  }
  return out;
}

function CategoryFields({ form, setForm, nodes, exclude, disabled }: { form: { name: string; slug: string; parentRef: string; intro: string }; setForm: (f: { name: string; slug: string; parentRef: string; intro: string }) => void; nodes: CategoryNode[]; exclude: Set<string>; disabled: boolean }) {
  return (
    <div className="form-grid">
      <label>
        Name
        <input value={form.name} required disabled={disabled} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label>
        Slug <small className="muted">empty = from the name</small>
        <input value={form.slug} disabled={disabled} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
      </label>
      <label className="span-2">
        Parent
        <select value={form.parentRef} disabled={disabled} onChange={(e) => setForm({ ...form, parentRef: e.target.value })}>
          <option value="">Top level</option>
          {nodes
            .filter((n) => !exclude.has(n.ref))
            .map((n) => (
              <option key={n.ref} value={n.ref}>
                {'  '.repeat(n.depth)}
                {n.depth ? '└ ' : ''}
                {n.name}
              </option>
            ))}
        </select>
      </label>
      <label className="span-2">
        Intro <small className="muted">shown on the category page</small>
        <textarea rows={3} value={form.intro} disabled={disabled} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
      </label>
    </div>
  );
}

function CategoryForm({ market, nodes, onClose }: { market: string; nodes: CategoryNode[]; onClose: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', slug: '', parentRef: '', intro: '' });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await runWrite(toast, 'admin.categories.create', { market, name: form.name, ...(form.slug ? { slug: form.slug } : {}), parentRef: form.parentRef || null, intro: form.intro || null }, `${form.name} created`);
    if (ok) onClose();
  };
  return (
    <Drawer title={`New category in ${market}`} onClose={onClose}>
      <form onSubmit={submit}>
        <CategoryFields form={form} setForm={setForm} nodes={nodes} exclude={new Set()} disabled={false} />
        <button type="submit" className="btn btn-primary">
          Create
        </button>
      </form>
    </Drawer>
  );
}

function CategoryDrawer({ node, market, nodes, canEdit, onClose }: { node: CategoryNode | null; market: string; nodes: CategoryNode[]; canEdit: boolean; onClose: () => void }) {
  const toast = useToast();
  const changes = useChanges();
  const [form, setForm] = useState({ name: '', slug: '', parentRef: '', intro: '' });
  useEffect(() => {
    if (node) setForm({ name: node.name, slug: node.slug, parentRef: node.parentRef ?? '', intro: node.intro ?? '' });
  }, [node]);
  const members = useOp<{ rows: MerchantOption[] }>('admin.categories.merchants', { ref: node?.ref ?? '' }, changes, Boolean(node));
  const [q, setQ] = useState('');
  const options = useOp<{ rows: MerchantOption[] }>('admin.merchants.options', { market, q: q || undefined, limit: 30 }, changes, Boolean(node));
  const [picked, setPicked] = useState<string[]>([]);
  if (!node) return null;

  const memberRefs = new Set((members.data?.rows ?? []).map((m) => m.ref));
  const save = async (event: FormEvent) => {
    event.preventDefault();
    await runWrite<{ changed: string[] }>(toast, 'admin.categories.update', { ref: node.ref, name: form.name, slug: form.slug, parentRef: form.parentRef || null, intro: form.intro }, (r) => (r.changed.length ? 'Category saved' : 'Nothing changed'));
  };
  const remove = async () => {
    const ok = await runWrite(toast, 'admin.categories.delete', { ref: node.ref }, `${node.name} deleted`);
    if (ok) onClose();
  };
  const assign = async (refs: string[], mode: 'add' | 'remove') => {
    const ok = await runWrite<{ changed: number }>(toast, 'admin.categories.assign', { categoryRef: node.ref, merchantRefs: refs, mode }, (r) => `${r.changed} merchant${r.changed === 1 ? '' : 's'} ${mode === 'add' ? 'added' : 'removed'}`);
    if (ok) setPicked([]);
  };

  return (
    <Drawer title={node.name} onClose={onClose} wide>
      <form onSubmit={save}>
        <CategoryFields form={form} setForm={setForm} nodes={nodes} exclude={subtree(nodes, node.ref)} disabled={!canEdit} />
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={!canEdit}>
            Save
          </button>
          <button type="button" className="btn btn-ghost btn-danger-ghost" disabled={!canEdit || node.children > 0 || node.merchants > 0} title={node.children || node.merchants ? 'Only empty categories can be deleted' : undefined} onClick={remove}>
            Delete
          </button>
        </div>
      </form>
      <div className="assign-grid">
        <section aria-labelledby="members-title">
          <h3 id="members-title" className="section-title">
            In this category ({members.data?.rows.length ?? 0})
          </h3>
          <ul className="member-list">
            {(members.data?.rows ?? []).map((m) => (
              <li key={m.ref}>
                <Link to={`/merchants/${m.ref}`}>{m.name}</Link>
                {canEdit ? (
                  <button type="button" className="btn btn-small btn-ghost btn-danger-ghost" onClick={() => assign([m.ref], 'remove')} aria-label={`Remove ${m.name}`}>
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
        {canEdit ? (
          <section aria-labelledby="add-title">
            <h3 id="add-title" className="section-title">
              Add merchants
            </h3>
            <input value={q} placeholder="Search merchants" aria-label="Search merchants to add" onChange={(e) => setQ(e.target.value)} />
            <ul className="member-list">
              {(options.data?.rows ?? [])
                .filter((m) => !memberRefs.has(m.ref))
                .map((m) => (
                  <li key={m.ref}>
                    <label className="inline-check">
                      <input type="checkbox" checked={picked.includes(m.ref)} onChange={(e) => setPicked(e.target.checked ? [...picked, m.ref] : picked.filter((r) => r !== m.ref))} />
                      {m.name}
                    </label>
                  </li>
                ))}
            </ul>
            <button type="button" className="btn btn-small btn-primary" disabled={!picked.length} onClick={() => assign(picked, 'add')}>
              Add {picked.length || ''} to {node.name}
            </button>
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}
